import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { PageLoader, ButtonLoader } from '../components/ui/loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, 
  FileText, 
  Play, 
  CheckCircle2, 
  AlertTriangle,
  Languages,
  Scan,
  Eye,
  Download,
  Edit3,
  Save,
  X,
  Network,
  Sparkles,
  Link2,
  Database,
  Layers,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentGraph from '../components/DocumentGraph';
import DocumentChat from '../components/DocumentChat';

const PARSERS = [
  { id: 'tesseract', name: 'Tesseract OCR', description: 'Local OCR engine' },
  { id: 'pdfjs', name: 'PDF.js', description: 'Text-based PDF extraction' },
  { id: 'openai_vision', name: 'OpenAI Vision', description: 'AI-powered extraction' },
  { id: 'google_docai', name: 'Google Document AI', description: 'Google Cloud service' },
  { id: 'azure_docint', name: 'Azure Doc Intelligence', description: 'Microsoft Azure service' },
];

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, token } = useAuth();
  
  const [document, setDocument] = useState(null);
  const [extractedTexts, setExtractedTexts] = useState([]);
  const [editedTexts, setEditedTexts] = useState({});
  const [validationResults, setValidationResults] = useState([]);
  const [references, setReferences] = useState([]);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [structuredDataList, setStructuredDataList] = useState([]);
  const [parserTemplates, setParserTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [extractingRefs, setExtractingRefs] = useState(false);
  const [extractingStructured, setExtractingStructured] = useState(false);
  const [selectedParser, setSelectedParser] = useState('pdfjs');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [aiMode, setAiMode] = useState(true);
  const [activeTab, setActiveTab] = useState('preview');
  const [editingPage, setEditingPage] = useState(null);
  const [editBuffer, setEditBuffer] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [viewMode, setViewMode] = useState('original');
  const [editingStructuredData, setEditingStructuredData] = useState(null);
  const [structuredEditBuffer, setStructuredEditBuffer] = useState({});
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    fetchDocument();
    fetchParserTemplates();
  }, [id]);

  const fetchDocument = async () => {
    setLoading(true);
    try {
      const [docRes, textsRes, resultsRes, editedRes, refsRes, graphRes, structuredRes] = await Promise.all([
        api().get(`/documents/${id}`),
        api().get(`/documents/${id}/extracted-texts`),
        api().get(`/documents/${id}/validation-results`),
        api().get(`/documents/${id}/edited-texts`).catch(() => ({ data: [] })),
        api().get(`/documents/${id}/references`).catch(() => ({ data: [] })),
        api().get(`/documents/${id}/reference-graph`).catch(() => ({ data: { nodes: [], edges: [] } })),
        api().get(`/documents/${id}/structured-data`).catch(() => ({ data: [] })),
      ]);
      setDocument(docRes.data);
      setExtractedTexts(textsRes.data);
      setValidationResults(resultsRes.data);
      setReferences(refsRes.data);
      setGraphData(graphRes.data);
      setStructuredDataList(structuredRes.data);
      
      const editedMap = {};
      editedRes.data.forEach(et => {
        editedMap[et.extracted_text_id] = et.edited_text;
      });
      setEditedTexts(editedMap);
    } catch (error) {
      toast.error('Failed to load document');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchParserTemplates = async () => {
    try {
      const response = await api().get('/parser-templates');
      setParserTemplates(response.data);
      if (response.data.length > 0) {
        setSelectedTemplate(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load parser templates');
    }
  };

  const handleParse = async () => {
    setParsing(true);
    try {
      const response = await api().post('/parse', {
        document_id: id,
        parser_type: selectedParser,
      });
      toast.success(`Document parsed successfully (${response.data.pages} pages)`);
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Parsing failed');
    } finally {
      setParsing(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const response = await api().post(`/validate/${id}`);
      toast.success(response.data.all_passed ? 'All validations passed!' : 'Validation complete with issues');
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleExtractReferences = async () => {
    setExtractingRefs(true);
    try {
      const response = await api().post(`/documents/${id}/extract-references`);
      toast.success(`Found ${response.data.references_found} references`);
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reference extraction failed');
    } finally {
      setExtractingRefs(false);
    }
  };

  const handleExtractStructured = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a parser template first');
      return;
    }
    
    setExtractingStructured(true);
    try {
      const start = await api().post(`/documents/${id}/extract-structured-async`, {
        template_id: selectedTemplate,
        parsing_method: aiMode ? 'ai' : 'regex_generated',
      });
      const taskId = start.data.task_id;
      let task = null;
      for (let i = 0; i < 240; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const tr = await api().get(`/extraction-tasks/${taskId}`);
        task = tr.data;
        if (task.status !== 'running') break;
      }
      if (!task || task.status === 'running') {
        toast.error('Extraction is taking too long — please try again');
        return;
      }
      if (task.status === 'failed') {
        toast.error(task.error || 'Structured extraction failed');
        return;
      }
      const data = task.result;
      const patternCount = Object.keys(data.generated_patterns || {}).length;
      if (!aiMode && patternCount > 0) {
        toast.success(`Generated ${patternCount} regex pattern(s) and extracted ${data.values?.length || 0} value(s)`);
      } else {
        toast.success(`Extracted ${data.values?.length || 0} fields`);
      }
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Structured extraction failed');
    } finally {
      setExtractingStructured(false);
    }
  };

  const handleStartEdit = (textItem) => {
    setEditingPage(textItem.id);
    setEditBuffer(editedTexts[textItem.id] || textItem.extracted_text);
  };

  const handleCancelEdit = () => {
    setEditingPage(null);
    setEditBuffer('');
  };

  const handleSaveEdit = async (textId) => {
    setSavingEdit(true);
    try {
      await api().put(`/documents/${id}/extracted-texts/${textId}/edit`, {
        edited_text: editBuffer
      });
      setEditedTexts(prev => ({ ...prev, [textId]: editBuffer }));
      toast.success('Text saved successfully');
      setEditingPage(null);
      setEditBuffer('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save edited text');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleStartStructuredEdit = (structuredData) => {
    setEditingStructuredData(structuredData.id);
    const buffer = {};
    structuredData.values.forEach(v => {
      buffer[v.field_name] = v.value;
    });
    setStructuredEditBuffer(buffer);
  };

  const handleSaveStructuredEdit = async (dataId, values) => {
    try {
      const updatedValues = values.map(v => ({
        ...v,
        value: structuredEditBuffer[v.field_name] ?? v.value
      }));
      
      await api().put(`/documents/${id}/structured-data/${dataId}`, updatedValues);
      toast.success('Structured data saved');
      setEditingStructuredData(null);
      fetchDocument();
    } catch (error) {
      toast.error('Failed to save structured data');
    }
  };

  const handleDownloadFile = async () => {
    try {
      const response = await api().get(`/documents/${id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleNodeClick = (node) => {
    if (node.id !== id) {
      navigate(`/documents/${node.id}`);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      uploaded: { className: 'bg-gray-100 text-gray-700', label: 'Uploaded' },
      parsed: { className: 'bg-blue-100 text-blue-700', label: 'Parsed' },
      validated: { className: 'bg-emerald-100 text-emerald-700', label: 'Validated' },
      issues_found: { className: 'bg-red-100 text-red-700', label: 'Issues Found' },
    };
    const config = variants[status] || { className: 'bg-gray-100', label: status };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const renderFieldValue = (value, fieldType) => {
    if (value === null || value === undefined) return <span className="text-muted-foreground italic">Not found</span>;
    if (fieldType === 'boolean') return value ? 'Yes' : 'No';
    if (fieldType === 'list' && Array.isArray(value)) {
      return (
        <ul className="list-disc pl-4">
          {value.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
    }
    return String(value);
  };

  if (loading) {
    return <PageLoader text="Loading document..." />;
  }

  if (!document) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="document-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/documents')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-3">
              {document.name}
              {getStatusBadge(document.status)}
            </h1>
            <p className="text-muted-foreground">{document.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setChatOpen(true)} data-testid="chat-btn">
            <MessageCircle className="h-4 w-4 mr-2" />
            Chat with Document
          </Button>
          <Button variant="outline" onClick={handleDownloadFile} data-testid="download-btn">
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      {/* Chat Dialog */}
      <DocumentChat
        documentId={id}
        documentName={document.name}
        open={chatOpen}
        onOpenChange={setChatOpen}
      />

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileText className="h-4 w-4" />
              <span className="text-sm">TYPE</span>
            </div>
            <p className="text-lg font-semibold">{document.file_type}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Languages className="h-4 w-4" />
              <span className="text-sm">LANGUAGE</span>
            </div>
            <p className="text-lg font-semibold uppercase">{document.language}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Scan className="h-4 w-4" />
              <span className="text-sm">PARSED PAGES</span>
            </div>
            <p className="text-lg font-semibold">{extractedTexts.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Link2 className="h-4 w-4" />
              <span className="text-sm">REFERENCES</span>
            </div>
            <p className="text-lg font-semibold">{references.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Layers className="h-4 w-4" />
              <span className="text-sm">STRUCTURES</span>
            </div>
            <p className="text-lg font-semibold">{structuredDataList.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Parser & Actions */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={selectedParser} onValueChange={setSelectedParser}>
              <SelectTrigger className="w-[200px]" data-testid="parser-select">
                <SelectValue placeholder="Select parser" />
              </SelectTrigger>
              <SelectContent>
                {PARSERS.map((parser) => (
                  <SelectItem key={parser.id} value={parser.id}>
                    {parser.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleParse} disabled={parsing} data-testid="parse-btn">
              {parsing ? <ButtonLoader /> : <Scan className="h-4 w-4 mr-2" />}
              Parse Document
            </Button>
            <Button onClick={handleValidate} disabled={validating} variant="secondary" data-testid="validate-btn">
              {validating ? <ButtonLoader /> : <Play className="h-4 w-4 mr-2" />}
              Run Validation
            </Button>
            <Button onClick={handleExtractReferences} disabled={extractingRefs} variant="outline" data-testid="extract-refs-btn">
              {extractingRefs ? <ButtonLoader /> : <Sparkles className="h-4 w-4 mr-2" />}
              Extract References
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="preview" className="gap-2">
            <Eye className="h-4 w-4" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="extracted" className="gap-2">
            <FileText className="h-4 w-4" />
            Extracted Text
          </TabsTrigger>
          <TabsTrigger value="structured" className="gap-2">
            <Database className="h-4 w-4" />
            Structured Data
          </TabsTrigger>
          <TabsTrigger value="validation" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Validation
          </TabsTrigger>
          <TabsTrigger value="references" className="gap-2">
            <Link2 className="h-4 w-4" />
            References
          </TabsTrigger>
          <TabsTrigger value="graph" className="gap-2">
            <Network className="h-4 w-4" />
            Graph
          </TabsTrigger>
        </TabsList>

        {/* Preview Tab */}
        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Document Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg bg-muted/30 min-h-[500px] flex items-center justify-center">
                {document.file_type === '.pdf' ? (
                  <iframe
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/file?token=${token}`}
                    className="w-full h-[600px] rounded-lg"
                    title="PDF Preview"
                  />
                ) : (
                  <div className="text-center p-8">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Preview not available for {document.file_type} files</p>
                    <Button variant="outline" className="mt-4" onClick={handleDownloadFile}>
                      Download to view
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Extracted Text Tab */}
        <TabsContent value="extracted">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Manrope']">Extracted Text</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant={viewMode === 'original' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setViewMode('original')}
                  >
                    Original OCR
                  </Button>
                  <Button 
                    variant={viewMode === 'edited' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={() => setViewMode('edited')}
                  >
                    Edited Version
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {extractedTexts.length > 0 ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-4">
                    {extractedTexts.map((text) => (
                      <div key={text.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">Page {text.page_num}</Badge>
                            <Badge variant="secondary">{text.parser_type}</Badge>
                            {editedTexts[text.id] && (
                              <Badge className="bg-blue-100 text-blue-700">Edited</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {editingPage === text.id ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                                  <X className="h-4 w-4" />
                                </Button>
                                <Button size="sm" onClick={() => handleSaveEdit(text.id)} disabled={savingEdit}>
                                  {savingEdit ? <ButtonLoader /> : <Save className="h-4 w-4 mr-1" />}
                                  Save
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handleStartEdit(text)}>
                                <Edit3 className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                        {editingPage === text.id ? (
                          <Textarea
                            value={editBuffer}
                            onChange={(e) => setEditBuffer(e.target.value)}
                            className="min-h-[200px] font-mono text-sm"
                          />
                        ) : (
                          <pre className="whitespace-pre-wrap text-sm font-mono bg-muted/30 p-3 rounded max-h-[300px] overflow-auto">
                            {viewMode === 'edited' && editedTexts[text.id] ? editedTexts[text.id] : text.extracted_text}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12">
                  <Scan className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No text extracted yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Select a parser and click "Parse Document"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Structured Data Tab */}
        <TabsContent value="structured">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-['Manrope']">Structured Data Extraction</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 rounded-md border px-3 py-1.5" data-testid="ai-mode-toggle-wrapper">
                    <Sparkles className={`h-4 w-4 ${aiMode ? 'text-primary' : 'text-muted-foreground'}`} />
                    <Label htmlFor="ai-mode-switch" className="text-sm cursor-pointer select-none">
                      AI Mode
                    </Label>
                    <Switch
                      id="ai-mode-switch"
                      checked={aiMode}
                      onCheckedChange={setAiMode}
                      data-testid="ai-mode-switch"
                    />
                  </div>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="w-[220px]" data-testid="structured-template-select">
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {parserTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleExtractStructured} disabled={extractingStructured || !selectedTemplate} data-testid="extract-structured-btn">
                    {extractingStructured ? <ButtonLoader /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Extract Structure
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2" data-testid="ai-mode-hint">
                {aiMode
                  ? 'AI Mode ON — the LLM reads the document and extracts field values directly.'
                  : 'AI Mode OFF — the LLM first generates regex patterns for each field, then extracts values using those patterns.'}
              </p>
            </CardHeader>
            <CardContent>
              {parserTemplates.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No parser templates defined</p>
                  <Button className="mt-4" variant="outline" onClick={() => navigate('/parser-templates')}>
                    Create Parser Template
                  </Button>
                </div>
              ) : structuredDataList.length > 0 ? (
                <div className="space-y-6">
                  {structuredDataList.map((structData) => {
                    // Group values by field type
                    const singleFields = structData.values.filter(v => v.field_type !== 'list_item');
                    const repeatingFields = structData.values.filter(v => v.field_type === 'list_item');
                    
                    // Group repeating fields by parent_field
                    const groupedRepeating = {};
                    repeatingFields.forEach(item => {
                      const parent = item.parent_field || item.field_name;
                      if (!groupedRepeating[parent]) {
                        groupedRepeating[parent] = [];
                      }
                      groupedRepeating[parent].push(item);
                    });
                    
                    return (
                      <Card key={structData.id} className="bg-muted/20">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base">{structData.template_name}</CardTitle>
                              <p className="text-xs text-muted-foreground">
                                Extracted: {new Date(structData.updated_at).toLocaleString()}
                                {repeatingFields.length > 0 && (
                                  <span className="ml-2 text-blue-600">
                                    • {repeatingFields.length} repeating items found
                                  </span>
                                )}
                              </p>
                            </div>
                            {editingStructuredData === structData.id ? (
                              <div className="flex gap-2">
                                <Button size="sm" variant="ghost" onClick={() => setEditingStructuredData(null)}>
                                  <X className="h-4 w-4" />
                                </Button>
                                <Button size="sm" onClick={() => handleSaveStructuredEdit(structData.id, structData.values)}>
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => handleStartStructuredEdit(structData)}>
                                <Edit3 className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            {/* Single Fields Section */}
                            {singleFields.length > 0 && (
                              <div className="space-y-4">
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                  Document Fields
                                </h4>
                                {singleFields.map((field, idx) => (
                                  <div key={idx} className="border-b pb-3 last:border-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-sm">{field.field_name}</span>
                                      {field.confidence !== null && field.confidence !== undefined && (
                                        <Badge variant="outline" className="text-xs">
                                          {(field.confidence * 100).toFixed(0)}%
                                        </Badge>
                                      )}
                                      {field.source_page && (
                                        <Badge variant="secondary" className="text-xs">
                                          Page {field.source_page}
                                        </Badge>
                                      )}
                                    </div>
                                    {editingStructuredData === structData.id ? (
                                      <Textarea
                                        value={structuredEditBuffer[`${field.field_name}_${idx}`] ?? field.value ?? ''}
                                        onChange={(e) => setStructuredEditBuffer(prev => ({
                                          ...prev,
                                          [`${field.field_name}_${idx}`]: e.target.value
                                        }))}
                                        className="mt-1 text-sm"
                                        rows={2}
                                      />
                                    ) : (
                                      <div className="text-sm text-muted-foreground bg-white p-2 rounded border">
                                        {renderFieldValue(field.value, field.field_type)}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Repeating Fields Section */}
                            {Object.keys(groupedRepeating).map(parentField => (
                              <div key={parentField} className="space-y-3">
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                                  {parentField}
                                  <Badge className="bg-blue-100 text-blue-700">
                                    {groupedRepeating[parentField].length} items
                                  </Badge>
                                </h4>
                                <div className="grid gap-3">
                                  {groupedRepeating[parentField].map((item, idx) => (
                                    <Card key={idx} className="bg-white border-l-4 border-l-blue-500">
                                      <CardContent className="py-3 px-4">
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <Badge variant="outline">
                                              #{item.occurrence_index || idx + 1}
                                            </Badge>
                                            {item.title && (
                                              <span className="font-medium text-sm">{item.title}</span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {item.confidence !== null && item.confidence !== undefined && (
                                              <Badge variant="secondary" className="text-xs">
                                                {(item.confidence * 100).toFixed(0)}%
                                              </Badge>
                                            )}
                                            {item.source_page && (
                                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                                Page {item.source_page}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                        {editingStructuredData === structData.id ? (
                                          <Textarea
                                            value={structuredEditBuffer[`${parentField}_${idx}`] ?? item.value ?? ''}
                                            onChange={(e) => setStructuredEditBuffer(prev => ({
                                              ...prev,
                                              [`${parentField}_${idx}`]: e.target.value
                                            }))}
                                            className="text-sm"
                                            rows={3}
                                          />
                                        ) : (
                                          <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                                            {item.value}
                                          </div>
                                        )}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No structured data extracted yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Select a template and click "Extract Structure"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Validation Tab */}
        <TabsContent value="validation">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Validation Results</CardTitle>
            </CardHeader>
            <CardContent>
              {validationResults.length > 0 ? (
                <div className="space-y-3">
                  {validationResults.map((result) => (
                    <div 
                      key={result.id}
                      className={`border rounded-lg p-4 ${result.passed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {result.passed ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium">{result.rule_name}</p>
                            {result.details?.message && (
                              <p className="text-sm text-muted-foreground">{result.details.message}</p>
                            )}
                          </div>
                        </div>
                        {result.match_percentage !== null && (
                          <Badge variant="outline">{result.match_percentage?.toFixed(1)}% match</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No validation results yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* References Tab */}
        <TabsContent value="references">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Document References</CardTitle>
            </CardHeader>
            <CardContent>
              {references.length > 0 ? (
                <div className="space-y-3">
                  {references.map((ref) => (
                    <div key={ref.id} className={`border rounded-lg p-4 ${ref.matched ? 'border-blue-200 bg-blue-50' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={ref.matched ? 'default' : 'secondary'}>{ref.reference_type}</Badge>
                        {ref.matched && <Badge className="bg-green-100 text-green-700">Matched</Badge>}
                      </div>
                      <p className="font-medium">{ref.reference_text}</p>
                      <p className="text-sm text-muted-foreground mt-1 italic">"{ref.context}"</p>
                      {ref.matched && ref.target_document_name && (
                        <Button variant="link" className="p-0 h-auto mt-2" onClick={() => navigate(`/documents/${ref.target_document_id}`)}>
                          → View: {ref.target_document_name}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Link2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No references extracted yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Graph Tab */}
        <TabsContent value="graph">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Document Reference Graph</CardTitle>
            </CardHeader>
            <CardContent>
              {graphData.nodes.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <DocumentGraph 
                    nodes={graphData.nodes}
                    edges={graphData.edges}
                    onNodeClick={handleNodeClick}
                    width={900}
                    height={500}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <Network className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No reference graph available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
